import React, { useState, useEffect } from 'react';
import {
  Layout,
  Breadcrumb,
  Input,
  Avatar,
  Badge,
  Dropdown,
  Menu,
  Button,
  Tooltip,
  Tag,
  Space,
  Modal
} from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  BellOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  DashboardOutlined,
  ExclamationCircleOutlined,
  NotificationOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Navbar.css';

const { Header } = Layout;
const { Search } = Input;

const Navbar = ({ collapsed, onCollapse, showSidebarToggle = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [bellAnimation, setBellAnimation] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    // Simulate receiving notifications
    const mockNotifications = [
      {
        id: 1,
        title: 'Equipment Maintenance Due',
        message: 'Excavator EXC-001 requires scheduled maintenance',
        type: 'warning',
        time: '10 minutes ago',
        read: false
      },
      {
        id: 2,
        title: 'Block Completion',
        message: 'S2B8 block has reached 85% completion',
        type: 'success',
        time: '1 hour ago',
        read: false
      },
      {
        id: 3,
        title: 'Safety Alert',
        message: 'High winds detected in mining area',
        type: 'danger',
        time: '2 hours ago',
        read: true
      },
      {
        id: 4,
        title: 'Shift Change',
        message: 'Night shift starting in 30 minutes',
        type: 'info',
        time: '3 hours ago',
        read: true
      }
    ];
    setNotifications(mockNotifications);

    // Animate bell for new notifications
    const hasUnread = mockNotifications.some(n => !n.read);
    if (hasUnread) {
      setBellAnimation(true);
      setTimeout(() => setBellAnimation(false), 1000);
    }
  }, []);

  const handleSearch = (value) => {
    console.log('Search:', value);
    // Implement search functionality
  };

  const handleLogout = () => {
    Modal.confirm({
      title: 'Confirm Logout',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to logout?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        logout();
        navigate('/login');
      }
    });
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    setNotifications(notifications.map(n => 
      n.id === notification.id ? { ...n, read: true } : n
    ));
    
    // Handle notification click based on type
    switch (notification.type) {
      case 'warning':
        navigate('/equipment');
        break;
      case 'success':
        navigate('/planning');
        break;
      case 'danger':
        navigate('/safety');
        break;
      default:
        break;
    }
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const userMenuItems = [
    {
      key: 'profile',
      label: (
        <div className="navbar-dropdown-item">
          <UserOutlined className="navbar-dropdown-item-icon" />
          <span className="navbar-dropdown-item-text">Profile</span>
        </div>
      ),
      onClick: () => navigate('/profile')
    },
    {
      key: 'settings',
      label: (
        <div className="navbar-dropdown-item">
          <SettingOutlined className="navbar-dropdown-item-icon" />
          <span className="navbar-dropdown-item-text">Settings</span>
        </div>
      ),
      onClick: () => navigate('/settings')
    },
    { type: 'divider', className: 'navbar-dropdown-divider' },
    {
      key: 'logout',
      label: (
        <div className="navbar-dropdown-item">
          <LogoutOutlined className="navbar-dropdown-item-icon" />
          <span className="navbar-dropdown-item-text">Logout</span>
        </div>
      ),
      onClick: handleLogout
    }
  ];

  const notificationMenuItems = [
    {
      key: 'group',
      type: 'group',
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Notifications</span>
          <Button type="link" size="small" onClick={markAllAsRead}>
            Mark all as read
          </Button>
        </div>
      ),
      children: notifications.length > 0 ? notifications.map(notification => ({
        key: notification.id,
        label: (
          <div 
            style={{ 
              padding: '12px 16px',
              borderLeft: `4px solid ${
                notification.type === 'warning' ? '#faad14' :
                notification.type === 'success' ? '#52c41a' :
                notification.type === 'danger' ? '#ff4d4f' : '#1890ff'
              }`,
              backgroundColor: notification.read ? '#fff' : '#f6ffed',
              cursor: 'pointer'
            }}
            onClick={() => handleNotificationClick(notification)}
          >
            <div style={{ 
              fontWeight: notification.read ? 'normal' : '600',
              color: 'rgba(0, 0, 0, 0.85)',
              marginBottom: 4
            }}>
              {notification.title}
            </div>
            <div style={{ 
              fontSize: 12,
              color: 'rgba(0, 0, 0, 0.65)',
              marginBottom: 4
            }}>
              {notification.message}
            </div>
            <div style={{ 
              fontSize: 11,
              color: 'rgba(0, 0, 0, 0.45)',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              <ClockCircleOutlined />
              {notification.time}
              {!notification.read && (
                <Badge dot style={{ marginLeft: 'auto' }} />
              )}
            </div>
          </div>
        )
      })) : [
        {
          key: 'no-notifications',
          disabled: true,
          label: (
            <div style={{ textAlign: 'center', padding: 16, color: 'rgba(0, 0, 0, 0.45)' }}>
              No notifications
            </div>
          )
        }
      ]
    }
  ];

  const getBreadcrumbItems = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    const items = [
      {
        key: 'home',
        title: (
          <>
            <DashboardOutlined />
            <span style={{ marginLeft: 4 }}>Home</span>
          </>
        )
      }
    ];

    pathnames.forEach((name, index) => {
      const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
      const isLast = index === pathnames.length - 1;
      
      let breadcrumbName = name.charAt(0).toUpperCase() + name.slice(1);
      
      // Custom names for specific routes
      if (name === 'operations') breadcrumbName = 'Operations';
      if (name === 'costing') breadcrumbName = 'Cost Analysis';
      if (name === 'planning') breadcrumbName = 'Mine Planning';
      
      items.push({
        key: name,
        title: isLast ? (
          <span>{breadcrumbName}</span>
        ) : (
          <a href={routeTo}>{breadcrumbName}</a>
        )
      });
    });

    return items;
  };

  const getPageTitle = () => {
    const path = location.pathname.split('/')[1] || 'dashboard';
    const titles = {
      'dashboard': 'Dashboard',
      'planning': 'Mine Planning',
      'operations': 'Operations',
      'costing': 'Cost Analysis',
      'equipment': 'Equipment Management',
      'reports': 'Reports & Analytics',
      'personnel': 'Personnel Management',
      'safety': 'Safety & Compliance',
      'profile': 'User Profile',
      'settings': 'System Settings'
    };
    return titles[path] || 'Mine Planning System';
  };

  const getInitials = () => {
    if (!user) return 'U';
    const name = user.firstName && user.lastName 
      ? `${user.firstName} ${user.lastName}`
      : user.username;
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username;
  };

  const getUserRole = () => {
    if (!user) return 'Guest';
    return user.role.charAt(0).toUpperCase() + user.role.slice(1);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <Header className="navbar">
      <div className="navbar-left">
        {showSidebarToggle && (
          <div
            className="navbar-toggle"
            onClick={() => onCollapse(!collapsed)}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </div>
        )}
        
        <h2 className="navbar-title">{getPageTitle()}</h2>
        
        <Breadcrumb className="navbar-breadcrumb" items={getBreadcrumbItems()} />
      </div>

      <div className="navbar-right">
        <Search
          className="navbar-search"
          placeholder="Search blocks, equipment, reports..."
          prefix={<SearchOutlined />}
          onSearch={handleSearch}
          allowClear
          style={{ width: 240 }}
        />

        <div className="quick-actions">
          <Tooltip title="Safety Dashboard">
            <div 
              className="quick-action-btn"
              onClick={() => navigate('/safety')}
            >
              <SafetyCertificateOutlined />
            </div>
          </Tooltip>
        </div>

        <div className="status-indicator">
          <div className="status-dot" />
          <span>All Systems Operational</span>
        </div>

        <Dropdown
          menu={{ items: notificationMenuItems, className: 'navbar-dropdown', style: { width: 320 } }}
          trigger={['click']}
          placement="bottomRight"
          open={showNotifications}
          onOpenChange={setShowNotifications}
        >
          <Badge count={unreadCount} size="small">
            <div className={`navbar-notification ${bellAnimation ? 'navbar-notification-new' : ''}`}>
              <BellOutlined style={{ fontSize: 18 }} />
            </div>
          </Badge>
        </Dropdown>

        <Dropdown menu={{ items: userMenuItems, className: 'navbar-dropdown' }} trigger={['click']} placement="bottomRight">
          <div className="navbar-user">
            <Avatar 
              className="user-avatar"
              size="default"
              style={{ backgroundColor: '#1890ff' }}
            >
              {getInitials()}
            </Avatar>
            <div className="user-info">
              <div className="user-name">{getUserDisplayName()}</div>
              <div className="user-role">{getUserRole()}</div>
            </div>
          </div>
        </Dropdown>
      </div>
    </Header>
  );
};

export default Navbar;